// 1. LoggingConcessionsComponent.jsx
import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

const LoggingConcessionsComponent = ({
  isVisible,
  opacity = 100,
  id,
  mapInstance,
  popupMapInstance
}) => {
  const popupRef = useRef(null);
  const layerA = `${id}-a`;
  const onMouseMove = useRef(null);
  const onMouseLeave = useRef(null);

  useEffect(() => {
    if (!mapInstance || !popupMapInstance) return;

    const initializeLayer = async () => {
      try {
        // Remove existing layers if they exist
        if (mapInstance.getLayer(layerA)) {
          mapInstance.removeLayer(layerA);
        }
        if (mapInstance.getLayer(`${layerA}-outline`)) {
          mapInstance.removeLayer(`${layerA}-outline`);
        }
        if (mapInstance.getSource(layerA)) {
          mapInstance.removeSource(layerA);
        }

        // Add source
        mapInstance.addSource(layerA, {
          type: 'vector',
          tiles: [
            'https://tiles.globalforestwatch.org/gfw_logging/v2020/default/{z}/{x}/{y}.pbf'
          ],
          minzoom: 0,
          maxzoom: 12
        });

        // Add fill layer
        mapInstance.addLayer({
          id: layerA,
          type: 'fill',
          source: layerA,
          'source-layer': 'gfw_logging',
          paint: {
            'fill-color': 'rgb(255, 219, 143)',
            'fill-opacity': opacity / 100,
            'fill-outline-color': 'rgb(204, 175, 114)'
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        });

        // Add outline layer
        mapInstance.addLayer({
          id: `${layerA}-outline`,
          type: 'line',
          source: layerA,
          'source-layer': 'gfw_logging',
          paint: {
            'line-color': 'rgb(204, 175, 114)',
            'line-width': 1,
            'line-opacity': opacity / 100
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        });

        onMouseMove.current = (e) => {
          mapInstance.getCanvas().style.cursor = 'pointer';

          const features = e.features;
          if (features.length > 0) {
            const { properties } = features[0];

            if (popupRef.current) {
              popupRef.current.remove();
            }

            const content = `
              <div style="font-family: sans-serif; padding: 8px;">
                <div><strong>Name:</strong> ${properties.name || 'N/A'}</div>
              </div>
            `;

            popupRef.current = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              maxWidth: '300px',
              className: 'logging-concession-popup'
            })
              .setLngLat(e.lngLat)
              .setHTML(content)
              .addTo(popupMapInstance);
          }
        };

        onMouseLeave.current = () => {
          mapInstance.getCanvas().style.cursor = '';
          if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
          }
        };

        mapInstance.on('mousemove', layerA, onMouseMove.current);
        mapInstance.on('mouseleave', layerA, onMouseLeave.current);

      } catch (err) {
        console.error('Error initializing logging concessions layer:', err);
      }
    };

    if (isVisible) {
      initializeLayer();
    }

    return () => {
      if (mapInstance) {
        if (onMouseMove.current) {
          mapInstance.off('mousemove', layerA, onMouseMove.current);
        }
        if (onMouseLeave.current) {
          mapInstance.off('mouseleave', layerA, onMouseLeave.current);
        }

        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        if (mapInstance.getLayer(`${layerA}-outline`)) {
          mapInstance.removeLayer(`${layerA}-outline`);
        }
        if (mapInstance.getLayer(layerA)) {
          mapInstance.removeLayer(layerA);
        }
        if (mapInstance.getSource(layerA)) {
          mapInstance.removeSource(layerA);
        }
      }
    };
  }, [isVisible, opacity, mapInstance, popupMapInstance, id, layerA]);

  return null;
};

export default LoggingConcessionsComponent;
